import clsx from "clsx";
import { useEffect, useRef } from "react";

import { COLORS_VALUES } from "@shared/constants";
import type { SliderCardBase } from "@shared/content-types/schemas/sections.schema";
import { useImageMaskRef } from "@shared/hooks/use-image-mask-ref";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { Image } from "@shared/ui/image";
import { InfoBadges } from "@shared/ui/info-badges";
import { Body } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import s from "./slider-card.module.scss";

export type SliderCardProps = SliderCardBase & {
  variant: "primary" | "secondary";
  context?: "slider" | "cultural-objects";
  className?: string;
  onClick?: () => void;
  active?: boolean;
};

export const SliderCard = ({
  image,
  location,
  time,
  title,
  description,
  className,
  icon,
  link,
  onClick,
  variant = "secondary",
  context = "slider",
  active = false,
}: SliderCardProps) => {
  const isPrimary = variant === "primary";
  const mods = mod(s, {
    variant: isPrimary ? "primary" : "secondary",
  });

  const $imageRef = useImageMaskRef();
  const $bottom = useRef<HTMLDivElement | null>(null);
  const $title = useRef<HTMLDivElement | null>(null);

  const style: React.CSSProperties & {
    ["--hover-card-color"]: string;
    ["--hover-text-color"]: string;
    cursor: string;
  } = {
    "--hover-card-color":
      COLORS_VALUES[icon.fillColor as keyof typeof COLORS_VALUES],
    "--hover-text-color":
      context !== "cultural-objects"
        ? COLORS_VALUES[icon.strokeColor as keyof typeof COLORS_VALUES]
        : "var(--c-milk)",
    cursor: isPrimary && link ? "pointer" : "inherit",
  };

  useEffect(() => {
    const resize = () => {
      if ($bottom.current && $title.current) {
        $bottom.current.style.setProperty(
          "--card-title-height",
          `${$title?.current.clientHeight || 0}px`,
        );
      }
    };

    resize();

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Button
      href={link}
      onClick={onClick}
      className={clsx(
        s.root,
        mods,
        className,
        {
          [s.active]: active,
        },
        s[`context-${context}`],
      )}
      style={style}
    >
      <div className={s.top}>
        <InfoBadges className={s.info} location={location} time={time} />
      </div>
      <div className={clsx(s.imageWrapper, mods)}>
        <Image
          className={s.imageBox}
          src={image?.url || "slider image"}
          alt={title}
          ref={$imageRef}
        />
      </div>

      <div className={s.bottom} ref={$bottom}>
        <div className={s.text}>
          <p className={s.title} ref={$title}>
            {title}
          </p>
          {isPrimary && description && (
            <Body size="caption" className={clsx(s.description, mods)}>
              {description}
            </Body>
          )}
        </div>
        {isPrimary && icon?.icon?.name && (
          <div className={s.iconWrapper}>
            <BrandIcon
              className={s.icon}
              name={icon.icon.name}
              fillColor={icon.fillColor}
              strokeColor={icon.strokeColor}
              svg_code={icon.uicon?.svg_code}
            />
            <BrandIcon
              className={clsx(s.icon, s.iconArrow)}
              name="arrow"
              fillColor={
                context !== "cultural-objects" ? icon.strokeColor : "milk"
              }
              strokeColor={icon.fillColor}
            />
          </div>
        )}
      </div>
    </Button>
  );
};
